import { ErrorHandler, Injectable, inject } from "@angular/core";
import { Auth, User, authState } from "@angular/fire/auth";
import {
  Firestore,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
} from "@angular/fire/firestore";
import { BehaviorSubject, lastValueFrom, take } from "rxjs";
import { ALERT_TYPE, AlertService } from "../common/services/alert.service";
import { LoginService } from "../login/login.service";

export interface IUserData {
  [key: string]: string | unknown;
  customItems: Record<string, unknown>;
  baseItems: Record<string, unknown>;
}

@Injectable({ providedIn: "root" })
export class UserService {
  public lightCachedUserData = new BehaviorSubject<IUserData | undefined>(
    undefined
  );
  public autoSaveVCard: boolean | undefined;
  public cardID$ = new BehaviorSubject<string | null>(null);

  private firestore = inject(Firestore);
  private auth = inject(Auth);
  private alertService = inject(AlertService);
  private loginService = inject(LoginService);
  private forbidenKeys: string[] = [
    "auth_uid",
    "active",
    "autoSaveVcard",
    "creationDate",
    "firstAccess",
  ];
  private cardID: string | undefined;

  /**
   * Retorna os dados do usuário como '<IUserData>'.
   * @return {IUserData}
   */
  async getUserData(): Promise<IUserData> {
    const userAuthUID = (await lastValueFrom(
      authState(this.auth).pipe(take(1))
    )) as unknown as User;
    const { uid } = userAuthUID;
    const q = query(
      collection(this.firestore, "cards"),
      where("auth_uid", "==", uid)
    );
    const userDataRef = await getDocs(q);
    const userData: any = this.handleLegacyData(userDataRef.docs[0].data());

    this.cardID = userDataRef.docs[0].id;
    /** @todo melhorar isso, 2 propriedades iguais */
    this.cardID$.next(userDataRef.docs[0].id);
    this.autoSaveVCard = userData?.["autoSaveVcard"];
    const data = this.sortUserData(userData) as IUserData;
    this.lightCachedUserData.next(data as IUserData);
    return data;
  }

  /**
   * Retorna os dados do usuário como `<IUserData>`
   * utilizando paramentro.
   * @param {string} cardID do cart~ao
   * @return {IUserData}
   */
  async getUserDataByCardID(cardID: string): Promise<IUserData> {
    const cardRef = doc(this.firestore, `cards/${cardID}`);
    const userDataRef = await getDoc(cardRef);
    const userData = userDataRef.data();
    const data = this.sortUserData(
      userData as Record<string, unknown>
    ) as IUserData;
    return data;
  }

  /**
   * @description
   * Separa os dados que foram criados pelo usuário e os dados
   * de base da plataforma.
   * @param {IUserData} userData
   * @return {IUserData}
   */
  sortUserData(userData: Record<string, unknown>): IUserData {
    const customData = {} as Record<string, unknown>;
    const baseData = {} as Record<string, unknown>;

    Object.keys(userData).forEach((key: string) => {
      if (key.startsWith("CUS_")) {
        customData[key] = userData[key];
      } else if (this.forbidenKeys.includes(key)) {
        delete userData[key];
      } else {
        baseData[key] = userData[key];
      }
    });

    const data = {} as IUserData;
    data.baseItems = baseData;
    data.customItems = customData;
    return data;
  }

  async saveData(userCardData: Record<string, unknown>): Promise<any> {
    const docRef = doc(this.firestore, `cards/${this.cardID}`);
    const objWithoutCountryCodes = this.setCountryCodesToNull(userCardData);
    const finalObj = this.replaceUndefinedWithNull(objWithoutCountryCodes);

    /** Consulta no objeto os valores, e redefine o valor (com máscara) em com
     * um novo valor sem máscara, apenas substituindo caracteres especiais e espaços.
     */
    if (finalObj["whatsapp"])
      finalObj["whatsapp"] = String(finalObj["whatsapp"])?.replace(/\D/g, "");
    else if (finalObj["telegram"])
      finalObj["telegram"] = String(finalObj["telegram"])?.replace(/\D/g, "");

    try {
      this.lightCachedUserData.next(userCardData as IUserData);
      return await setDoc(docRef, finalObj, { merge: true });
    } catch (error) {
      console.error("Error on saveData: ", error);
    }
  }

  /**
   * Get Card ID
   * @description Method that returns the current `cardID`.
   * @return {string} cardID
   */
  getCardID(): string {
    return this.cardID as string;
  }

  /**
   * @description
   * Fetches the username based on the current `cardID` and returns it in case exists.
   * @return {{cardID: string, uid: string}}
   */
  async getUsername(): Promise<string | undefined> {
    const q = query(
      collection(this.firestore, "usernames"),
      where("docID", "==", this.cardID)
    );
    const docs = await getDocs(q);
    return docs.empty ? undefined : (docs.docs[0].id as string);
  }

  /**
   * Set User name
   * @description
   * Este Método tem a funcionalidade criar novas gravações dentro do banco de dados dos "username". Este método
   * faz algumas verificações antes de executar esta ação.
   * 1. Se o parametro de `shouldRemove` é passado como `true` retorna o método
   * `deleteUsername()`. Isso pressupõe que o usuário deseja excluir seu "username".
   * 2. Se o parametro de `shoudRemove`é passado como `false`, faz uma solicitação ao servidor
   * para o "username" para verificar se o ele já existe, se `true` retornará um error ao usuário
   * informando que o nome já está sendo utilizado
   * 3. Cria um novo registro ao DB. O ID é o "username" escolhido pelo usuário e
   * o payload é { docID: string, cardID: string }.
   * @param {string} username choosen name by the user
   * @param {boolean} shouldRemove default to false.
   * @return {Promise<void>}
   */
  async setUsernameOrDelete(
    username: string | null | undefined,
    shouldRemove: boolean = false
  ): Promise<void> {
    try {
      /** Condição para descobrir se o o Toggle do URL Amigável foi desabilitado, se
       *  desabilitado, ele inicia a condição fazendo um delete do nome de usuário
       */
      if (shouldRemove) {
        this.alertService.showAlert(
          ALERT_TYPE.SUCCESS,
          "COMMON.ALERTS.informationDeleteUsername"
        );
        return this.deleteUsername();
      }
      const doesUserNameExists = await this.checkIfUsernameExists(
        username?.toLowerCase() as string
      );

      /** Condição para verificar se o nome de usuário informado já existe no
       * sistema, se existe, ele informa um erro para o usuário dizendo que o nome de usuário já foi
       * escolhido.
       */
      if (doesUserNameExists) {
        this.alertService.showAlert(
          ALERT_TYPE.ERROR,
          "COMMON.ALERTS.usernameUnavailable"
        );
      } else {
        const docRef = doc(
          this.firestore,
          `usernames/${username?.toLowerCase()}`
        );
        /** Faz a verificação do nome de usuário para verificar se  */
        await setDoc(docRef, {
          docID: this.cardID,
          uid: this.loginService.authUID,
        });
        await this.alertService.showAlert(
          ALERT_TYPE.SUCCESS,
          "COMMON.ALERTS.informationSavedSuccessfully"
        );
      }
    } catch (error) {
      await this.alertService.showAlert(
        ALERT_TYPE.ERROR,
        "COMMON.ALERTS.errorGeneric"
      );
    }
  }

  /**
   * Método de ajuda para remover um "username" do DB.
   * Verifica se existe o username no DB e se o UID deste documento é o mesmo
   * do token de autenticação. Caso positivo, remove o documento.
   * O "preExistingUsername" é necessário em caso o usuário "ligue" o toggle e
   * coloque um valor e antes de salvar, desligue o toggle. Desta maneira, não
   * corremos o risco de erros de exception.
   * @return {}
   */
  async deleteUsername(): Promise<void> {
    try {
      const preExistingUsername = await this.getUsername();
      if (preExistingUsername) {
        const delRef = doc(this.firestore, `usernames/${preExistingUsername}`);
        /** Verifica se o "docID" e o "uid" são do mesmo "username", evitando
         * futuros problemas com segurança e evitando bugs de delete em massa
         */
        const resData = (await getDoc(delRef)).data() as {
          docID: string;
          uid: string;
        };
        if (
          resData.uid === this.loginService.authUID ||
          resData.docID === this.loginService.authUID
        ) {
          return await deleteDoc(delRef);
        }
      }
    } catch (error) {
      console.error("Error to deleteUsername:", error);

      await this.alertService.showAlert(
        ALERT_TYPE.ERROR,
        "COMMON.ALERTS.errorGeneric"
      );
    }
  }

  async checkIfUsernameExists(username: string): Promise<boolean> {
    const docRef = doc(this.firestore, `usernames/${username}`);
    const data = await getDoc(docRef);
    return data.exists();
  }

  async getRedirect(): Promise<Record<string, string> | undefined> {
    if (!this.getCardID()) {
      await this.getUserData();
    }
    const ref = doc(this.firestore, `redirects/${this.getCardID()}`);
    const data = await getDoc(ref);
    return data.exists() ? data.data() : undefined;
  }

  async setRedirect(dataURL: string, shouldRemove = false): Promise<void> {
    if (!this.getCardID()) {
      await this.getUserData();
    }
    const ref = doc(this.firestore, `redirects/${this.getCardID()}`);
    const savedData = setDoc(ref, { redirectURL: dataURL });
    try {
      /** if the user uncked the redirect, it will remove it from DB */
      if (shouldRemove) {
        const relRef = doc(this.firestore, `redirects/${this.cardID}`);
        await this.alertService.showAlert(
          ALERT_TYPE.SUCCESS,
          "COMMON.ALERTS.informationSavedSuccessfully"
        );
        return await deleteDoc(relRef);
      }
      await this.alertService.showAlert(
        ALERT_TYPE.SUCCESS,
        "COMMON.ALERTS.informationSavedSuccessfully"
      );
      return await savedData;
    } catch (error) {
      await this.alertService.showAlert(ALERT_TYPE.ERROR, "Error");
      throw new ErrorHandler().handleError(error);
    }
  }

  /**
   * @description
   * Method that defines if the user's card should be auto saved or not.
   * The `autoSaveVCard` property on this class is already set when the
   * service is initially instanciated.
   * @param {boolean} shouldAutoSave
   */
  async setAutoSaveCard(shouldAutoSave: boolean): Promise<void> {
    const cardRef = doc(this.firestore, `cards/${this.cardID}`);
    try {
      await setDoc(cardRef, { autoSaveVcard: shouldAutoSave }, { merge: true });
      await this.alertService.showAlert(
        ALERT_TYPE.SUCCESS,
        "COMMON.ALERTS.informationSavedSuccessfully"
      );
      this.autoSaveVCard = shouldAutoSave;
    } catch (error) {
      await this.alertService.showAlert(ALERT_TYPE.ERROR, "Error");
    }
  }

  /**
   * @description
   * This method will check if any property of a given object is `undefined`. If so,
   * it will change the `undefined` for `null`. Reason being because when documents
   * are updated on Firestore we are not using `update` but `set` instead with the
   * `merge` argument set to true.
   * Firestore doesn't accept `undefined` only `null`. Removing the property from
   * the payload that's sent, won't change the value on DB, that's why it needs to
   * have a `null` value.
   * @param {any} obj
   * @return {Record<string, unknown>}
   */
  private replaceUndefinedWithNull(obj: any): Record<string, unknown> {
    if (obj === null || typeof obj !== "object") {
      return obj;
    }
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (obj[key] === undefined) {
          obj[key] = null;
        } else if (typeof obj[key] === "object") {
          this.replaceUndefinedWithNull(obj[key]);
        }
      }
    }
    return obj;
  }

  /**
   * Método que verifica em contas antigas a existência de propriedades
   * com números do país para telefones. Caso existam, este método itera
   * este valor na propriedade do número principal e seta o valor "null"
   * para o campo anterior.
   * @param {Record<string, unknown>} data
   * @return {Record<string, unknown>}
   */
  private handleLegacyData(
    data: Record<string, unknown>
  ): Record<string, unknown> {
    const userData = data as any;
    let whatsAppCC = data?.["whatsappCountryCode"];
    let whatsAppNumber = data?.["whatsapp"];
    let telegramCC = data?.["telegramCountryCode"];
    let telegramNumber = data?.["telegram"];
    let smsCC = data?.["smsCountryCode"];
    let smsNumber = data?.["sms"];

    if (whatsAppCC && whatsAppCC !== "" && whatsAppNumber) {
      userData.whatsapp = ((whatsAppCC as string) + whatsAppNumber) as string;
    }
    if (telegramCC && telegramCC !== "" && telegramNumber) {
      userData.telegram = ((telegramCC as string) + telegramNumber) as string;
    }
    if (smsCC && smsCC !== "" && smsNumber) {
      userData.sms = ((smsCC as string) + smsNumber) as string;
    }
    if (data["whatsapp"])
      userData["whatsapp"] = String(data["whatsapp"]).replace(/\D/g, "");
    if (data["telegram"])
      userData["whatsapp"] = String(data["telegram"]).replace(/\D/g, "");

    return userData;
  }

  /**
   * Adiciona o valor de "null" para os country codes legado.
   * @param {any} data
   * @return {Record<string, unknown>}
   */
  private setCountryCodesToNull(data: any): Record<string, unknown> {
    const userData = data as any;
    userData.whatsappCountryCode = null;
    userData.telegramCountryCode = null;
    userData.smsCountryCode = null;
    return userData;
  }
}
